import { Organization, OrganizationService } from '@/api/';
import { action, atom } from 'nanostores';

/**
 * The current organization for the user, if present.
 */
export const $org = atom<Organization | undefined>();

/**
 * The organizations that this user has access to.
 */
export const $orgs = atom<Organization[]>([]);

/**
 * Main function to load organizations
 */
const loadOrganizations = async () => {
  console.log('Retrieving organizations...');

  const orgs = await OrganizationService.getOrganizations();

  $orgs.set(orgs);
  return orgs;
};

/**
 * Watch for route changes; if we haven't loaded the orgs, we try to load them.
 */
route.subscribe(async name => {
  if (name !== 'organizations' || ($orgs.value?.length ?? 0) > 0) {
    return;
  }

  await loadOrganizations();
});

/**
 * Ensure that the organization is loaded and selected.
 */
export const ensureOrganization = action(
  $org,
  'ensureOrganization',
  async (store, orgId: string) => {
    let org: Organization | undefined;

    if ($orgs.get().length === 0) {
      const orgs = await loadOrganizations();

      org = orgs.find(o => o.id === orgId);
    } else {
      org = $orgs.get().find(o => o.id === orgId);
    }

    $org.set(org);
  },

);

/**
 * Store for the organization logo URL and its expiration time.
 */

export const $orgLogoUrls = atom<{ [key: string]: { url: string; expiresAt: number } }>({});

/**
 * Fetch and store the organization logo URL with expiration.
 */
export const fetchOrgLogoUrl = action(
  $orgLogoUrls,
  'fetchOrgLogoUrl',
  async (store, orgId: string) => {
    const url = await OrganizationService.orgLogoSignedUrl(orgId);
    const expiresAt = Date.now() + 3600 * 1000; // 1 hour from now

    const currentUrls = store.get();
    store.set({ ...currentUrls, [orgId]: { url, expiresAt } });
  },
);


/**
 * Evict the organization logo URL from the store.
 */
export const evictOrgLogoUrl = action(
  $orgLogoUrls,
  'evictOrgLogoUrl',
  async (store, orgId: string) => {
    const currentUrls = store.get();
    delete currentUrls[orgId];
    store.set(currentUrls);
  },
);

/**
 * Check if the stored URL is expired and refetch if necessary.
 */
export const ensureOrgLogoUrl = action(
  $orgLogoUrls,
  'ensureOrgLogoUrl',
  async (store, orgId: string) => {
    const currentUrls = store.get();
    const current = currentUrls[orgId];

    if (!current || Date.now() > current.expiresAt) {
      await fetchOrgLogoUrl(orgId);
    }
  },
);

